import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import type { OnboardingStateDTO } from '@libs/dtos/merit/onboarding.dto';
import { probeVideoDuration } from '@services/merit/onboarding.service';
import { LucideModule } from '@shared/modules/lucide.module';

/** The stored-file shape of the contract (`OnboardingFileSchema`). */
export type OnboardingFile = NonNullable<OnboardingStateDTO['cv']>;

/** What the wizard receives once the file cleared type, size and duration. */
export interface AcceptedUpload {
	file: File;
	/** Only measured for video; null when the browser could not decode it. */
	durationSeconds: number | null;
}

/**
 * Single-file dropzone for the three upload steps (photo, CV, video).
 *
 * It owns the whole client-side gate — MIME type, byte size and, for the video,
 * the 60–90 s window read from the browser — and renders the failure inline
 * (M02 §6). The server re-validates everything; this only saves the round trip
 * and gives an immediate, specific message.
 */
@Component({
	selector: 'app-onboarding-dropzone',
	imports: [TranslocoModule, LucideModule],
	templateUrl: './onboarding-dropzone.component.html',
	styleUrl: './onboarding-dropzone.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { '[class.is-compact]': 'compact()' },
})
export class OnboardingDropzoneComponent {
	private readonly transloco = inject(TranslocoService);

	/**
	 * Three side by side in the Documents block (23/09): a smaller preview and
	 * tighter padding, so the photo does not stretch its column to twice the
	 * height of the other two.
	 */
	readonly compact = input(false);

	/** Drives the icon, the copy and which validations run. */
	readonly kind = input.required<'photo' | 'cv' | 'video'>();
	readonly mimeTypes = input.required<readonly string[]>();
	/**
	 * Extensiones aceptadas (`.pdf`, `.doc`…). Cuando se pasan, mandan sobre el
	 * `mimeTypes`: el tipo que declara el navegador para un `.docx` cambia según
	 * el sistema, y filtrar por extensión es lo único que el usuario reconoce.
	 */
	readonly extensions = input<readonly string[]>([]);
	readonly maxBytes = input.required<number>();
	/** Video only: the mandatory 60–90 s window. */
	readonly minSeconds = input<number | null>(null);
	readonly maxSeconds = input<number | null>(null);
	/** File already stored on the server, so the candidate can see / replace it. */
	readonly current = input<OnboardingFile | null>(null);
	readonly uploading = input(false);
	/** Error raised by the upload call itself (the local ones live here). */
	readonly serverError = input<string | null>(null);

	readonly accepted = output<AcceptedUpload>();
	/** Se eligió un archivo, valga o no: el padre borra el aviso del intento anterior (03/09 · 43). */
	readonly picked = output<void>();

	readonly dragging = signal(false);
	readonly localError = signal<string | null>(null);
	/** True while the duration of a picked video is being read. */
	readonly probing = signal(false);

	readonly accept = computed(() => {
		const extensions = this.extensions();
		return extensions.length ? extensions.join(',') : this.mimeTypes().join(',');
	});
	readonly maxMb = computed(() => Math.round(this.maxBytes() / (1024 * 1024)));
	readonly busy = computed(() => this.uploading() || this.probing());
	readonly error = computed(() => this.localError() ?? this.serverError());

	readonly icon = computed(() => {
		switch (this.kind()) {
			case 'photo':
				return 'Image';
			case 'video':
				return 'Video';
			default:
				return 'FileText';
		}
	});

	/** Human size of the stored file, for the "already uploaded" row. */
	readonly currentSize = computed(() => {
		const bytes = this.current()?.fileSize;
		if (!bytes) return '';
		const mb = bytes / (1024 * 1024);
		return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
	});

	onDragOver(event: DragEvent): void {
		event.preventDefault();
		this.dragging.set(true);
	}

	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		this.dragging.set(false);
	}

	onDrop(event: DragEvent): void {
		event.preventDefault();
		this.dragging.set(false);
		const file = event.dataTransfer?.files?.[0];
		if (file) void this.validate(file);
	}

	onPick(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		// Reset so re-picking the same file fires `change` again.
		input.value = '';
		if (file) void this.validate(file);
	}

	private t(key: string, params?: Record<string, unknown>): string {
		return this.transloco.translate(`merit.onboarding.upload.errors.${key}`, params);
	}

	/** Type → size → duration. The first failure wins and stops the upload. */
	private async validate(file: File): Promise<void> {
		this.localError.set(null);
		this.picked.emit();

		const name = file.name.toLowerCase();
		const extensions = this.extensions();
		if (extensions.length) {
			// Con extensiones declaradas se filtra solo por ellas: el servidor
			// comprueba después la firma del fichero, que es lo que no se falsea.
			if (!extensions.some((extension) => name.endsWith(extension))) {
				this.localError.set(this.t(`type.${this.kind()}`, { name: file.name }));
				return;
			}
		} else {
			const types = this.mimeTypes();
			// Some browsers report an empty type for exotic containers; fall back to
			// the extension so a legitimate file is not rejected on a missing header.
			const declared = (file.type || '').toLowerCase();
			const extensionOk = types.some((type) => name.endsWith(`.${type.split('/')[1]}`));
			if (declared ? !types.includes(declared) : !extensionOk) {
				this.localError.set(this.t(`type.${this.kind()}`, { name: file.name }));
				return;
			}
		}

		if (file.size > this.maxBytes()) {
			this.localError.set(this.t('size', { name: file.name, max: this.maxMb() }));
			return;
		}

		const min = this.minSeconds();
		const max = this.maxSeconds();
		if (min == null || max == null) {
			this.accepted.emit({ file, durationSeconds: null });
			return;
		}

		this.probing.set(true);
		const duration = await probeVideoDuration(file);
		this.probing.set(false);
		// Undecodable metadata: let it through and let the server be the judge.
		if (duration != null && (duration < min || duration > max)) {
			this.localError.set(this.t('duration', { seconds: Math.round(duration), min, max }));
			return;
		}
		this.accepted.emit({ file, durationSeconds: duration });
	}
}
